import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALERT_FREQUENCIES,
  ALERT_KIND_SPECS,
  type AlertChannel,
  type AlertFrequency,
  type IAlertSettingRow,
} from '@d-orca/shared/alert-kinds';
import { PickCombo } from '@components/ui/PickCombo/PickCombo';
import { Switch } from '@components/ui/Switch/Switch';
import { RestrictedAreaTitle, type ContractorsById } from './RestrictedAreaTitle';
import styles from './AlertRow.module.css';

export interface IAlertRowProps {
  row: IAlertSettingRow;
  onChannelChange: (channel: AlertChannel, on: boolean) => void;
  onFrequencyChange: (frequency: AlertFrequency) => void;
  /** The recipients control — the Alerts Hub's slot. */
  recipients?: ReactNode;
  /** Set on a restricted-area row: its title gets an Edit link-button. */
  onEditArea?: () => void;
  contractors?: ContractorsById;
}

/**
 * One alert setting: what it is, then — right-aligned — the host's recipients
 * control, the frequency where the kind has one, and a switch per channel the
 * kind can be delivered on. The row only reports changes; saving, confirming
 * and rolling back are the page's.
 */
export function AlertRow({ row, onChannelChange, onFrequencyChange, recipients, onEditArea, contractors }: IAlertRowProps) {
  const { t } = useTranslation();
  const spec = ALERT_KIND_SPECS[row.kind];
  const name = t(`alertSettings.kinds.${row.kind}.name`);

  return (
    <div className={styles.row}>
      <div className={styles.text}>
        {row.restrictedArea ? (
          <RestrictedAreaTitle area={row.restrictedArea} contractors={contractors} onEdit={onEditArea} />
        ) : (
          <>
            <span className={styles.name}>{name}</span>
            <span className={styles.description}>{t(`alertSettings.kinds.${row.kind}.description`)}</span>
          </>
        )}
      </div>
      <div className={styles.controls}>
        {recipients}
        {spec.defaultFrequency && (
          <div className={styles.frequency}>
            <PickCombo
              value={row.frequency ?? spec.defaultFrequency}
              options={ALERT_FREQUENCIES.map((f) => ({ value: f, label: t(`alertSettings.frequency.${f}`) }))}
              placeholder={t('alertSettings.frequency.label')}
              onPick={(value) => onFrequencyChange(value as AlertFrequency)}
              testId={`frequency-${row.key}`}
            />
          </div>
        )}
        {spec.channels.map((channel) => {
          const label = t(`alertSettings.channels.${channel}`);
          return (
            <label key={channel} className={styles.channel}>
              <span className={styles.channelLabel}>{label}</span>
              <Switch
                checked={row.channels[channel] === true}
                onCheckedChange={(on) => onChannelChange(channel, on)}
                ariaLabel={t('alertSettings.channelAria', { channel: label, alert: name })}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
